import React from 'react';
import { useMutation } from 'react-query';
import { Trans } from 'react-i18next';
import { useRequest } from '@datahub/api-client';
import { InvoiceMutationPropsInterface } from './InvoiceMutationProps.interface';
import { ApiOperation } from '../../../../api';
import useGeneralErrorHandler from '../../../error-handlers/useGeneralErrorHandler';
import useOnError from '../../../error-handlers/useOnError';
import { backInvoiceToReviewMutationKey } from '../../mutationKeys';
import { queryClient } from '../../../../queryClient';
import {
  getProcessedInvoicesQueryKey,
  getToApproveInvoicesQueryKey,
  getToVerifyInvoicesQueryKey,
} from '../../../queries/queryKeys';
import { useAlert } from '../../../../alert/Alert.provider';
import { ProcessedInvoiceActionPayloadInterface } from './interfaces/ProcessedInvoiceActionPayload.interface';

const useBackInvoiceToReviewMutation = ({ setOtherError }: InvoiceMutationPropsInterface = {}) => {
  const { showAlert } = useAlert();

  const { request } = useRequest(ApiOperation.BackInvoiceToReview);

  const generalErrorHandler = useGeneralErrorHandler(setOtherError);

  const { onError } = useOnError([], generalErrorHandler);

  return useMutation(
    async ({ invoiceId: id }: ProcessedInvoiceActionPayloadInterface) => {
      const res = await request({ urlParams: { id }, data: {} });

      return res.data;
    },
    {
      mutationKey: backInvoiceToReviewMutationKey,
      onSuccess: (_, { invoiceReference: name }: ProcessedInvoiceActionPayloadInterface) => {
        queryClient.invalidateQueries(getToVerifyInvoicesQueryKey);
        queryClient.invalidateQueries(getToApproveInvoicesQueryKey);
        queryClient.invalidateQueries(getProcessedInvoicesQueryKey);

        showAlert(
          <Trans
            i18nKey="invoices.tabs.processed.actions.backToReview.success"
            values={{ name }}
            components={{ bold: <strong /> }}
          />,
          'success',
        );
      },
      onError,
    },
  );
};

export default useBackInvoiceToReviewMutation;
